import React, {useCallback,useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import db, { auth } from '../firebase';
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  addDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import './APage.css';

const emptyIntern = {
  name: '',
  joiningDate: '',
  endingDate: '',
  domain: '',
  responsibilities: '',
  projects: '',
};

const emptyCertificate = {
  name: '',
  certificateId: '',
  grade: '',
  courseName: '',
  startDate: '',
  endDate: '',
  description: '',
  feePending: false,
};

const emptyProject = {
  title: '',
  description: '',
  category: '',
  client: '',
  technologies: '',
  imageUrl: '',
  liveUrl: '',
  githubUrl: '',
  status: 'Completed',
  featured: false,
};

const emptyReview = {
  customerName: "",
  company: "",
  review: "",
  rating: "5",
  photoUrl: "",
  service: "",
  featured: false,
  published: false,
  approved: false,
  status: "pending",
};

const emptyPost = {
  title: '',
  excerpt: '',
  content: '',
  category: '',
  imageUrl: '',
  link: '',
  published: true,
};

const APage = () => {
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [interns, setInterns] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [posts, setPosts] = useState([]);

  const [internForm, setInternForm] = useState(emptyIntern);
  const [certificateForm, setCertificateForm] = useState(emptyCertificate);
  const [projectForm, setProjectForm] = useState(emptyProject);
  const [reviewForm, setReviewForm] = useState(emptyReview);
  const [postForm, setPostForm] = useState(emptyPost);

  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showPostForm, setShowPostForm] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');

  const fetchCollection = async (collectionName) => {
    try {
      const snapshot = await getDocs(collection(db, collectionName));

      console.log(
        `✅ ${collectionName}: ${snapshot.size} document(s) loaded`
      );

      return snapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));
    } catch (error) {
      console.error(
        `❌ Error loading ${collectionName}:`,
        error
      );

      return [];
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      // Load every collection independently.
      // If one collection fails, the others will still load.
      const [
        bookingsData,
        contactsData,
        internsData,
        certificatesData,
        projectsData,
        reviewsData,
        postsData,
      ] = await Promise.all([
        fetchCollection('bookings'),
        fetchCollection('contacts'),
        fetchCollection('Interns'),
        fetchCollection('certificates'),
        fetchCollection('projects'),
        fetchCollection('reviews'),
        fetchCollection('posts'),
      ]);

      // Update dashboard state
      setBookings(bookingsData);
      setContacts(contactsData);
      setInterns(internsData);
      setCertificates(certificatesData);
      setProjects(projectsData);
      setReviews(reviewsData);
      setPosts(postsData);

      console.log('====================================');
      console.log('📊 DASHBOARD DATA');
      console.log('Bookings:', bookingsData.length);
      console.log('Contacts:', contactsData.length);
      console.log('Interns:', internsData.length);
      console.log('Certificates:', certificatesData.length);
      console.log('Projects:', projectsData.length);
      console.log('Reviews:', reviewsData.length);
      console.log('Posts:', postsData.length);
      console.log('====================================');
    } catch (error) {
      console.error('❌ Dashboard data error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* =========================
     DELETE FUNCTIONS
  ========================= */

  const handleDelete = async (collectionName, id, label, setter) => {
    if (!window.confirm(`Delete this ${label}?`)) return;

    try {
      await deleteDoc(doc(db, collectionName, id));

      setter((previous) =>
        previous.filter((item) => item.id !== id)
      );
    } catch (error) {
      console.error(`Failed to delete ${label}:`, error);
      alert(`Failed to delete ${label}.`);
    }
  };

  const handleDeleteBooking = (id) =>
    handleDelete('bookings', id, 'booking', setBookings);

  const handleDeleteContact = (id) =>
    handleDelete('contacts', id, 'contact message', setContacts);

  const handleDeleteIntern = (id) =>
    handleDelete('Interns', id, 'intern', setInterns);

  const handleDeleteCertificate = (id) =>
    handleDelete('certificates', id, 'certificate', setCertificates);

  const handleDeleteProject = (id) =>
    handleDelete('projects', id, 'project', setProjects);

  const handleDeleteReview = (id) =>
    handleDelete('reviews', id, 'review', setReviews);

  const handleDeletePost = (id) =>
    handleDelete('posts', id, 'post', setPosts);

  /* =========================
     FORM HANDLERS
  ========================= */

  const handleInternChange = (e) => {
    const { name, value } = e.target;

    setInternForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleCertificateChange = (e) => {
    const { name, value, type, checked } = e.target;

    setCertificateForm((previous) => ({
      ...previous,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleProjectChange = (e) => {
    const { name, value, type, checked } = e.target;

    setProjectForm((previous) => ({
      ...previous,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleReviewChange = (e) => {
    const { name, value, type, checked } = e.target;

    setReviewForm((previous) => ({
      ...previous,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePostChange = (e) => {
    const { name, value, type, checked } = e.target;

    setPostForm((previous) => ({
      ...previous,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  /* =========================
     ADD INTERN
  ========================= */

  const handleAddIntern = async (e) => {
    e.preventDefault();

    try {
      const newIntern = {
        ...internForm,
        responsibilities: internForm.responsibilities
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
        projects: internForm.projects
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
        createdAt: serverTimestamp(),
      };

      await addDoc(collection(db, 'Interns'), newIntern);

      setInternForm(emptyIntern);
      await fetchData();

      alert('Intern added successfully.');
    } catch (error) {
      console.error('Error adding intern:', error);
      alert('Failed to add intern.');
    }
  };

  /* =========================
     ADD CERTIFICATE
  ========================= */

  const handleAddCertificate = async (e) => {
    e.preventDefault();

    try {
      await addDoc(collection(db, 'certificates'), {
        ...certificateForm,
        createdAt: serverTimestamp(),
      });

      setCertificateForm(emptyCertificate);
      await fetchData();

      alert('Certificate added successfully.');
    } catch (error) {
      console.error('Error adding certificate:', error);
      alert('Failed to add certificate.');
    }
  };

  /* =========================
     ADD PROJECT
  ========================= */

  const handleAddProject = async (e) => {
    e.preventDefault();

    try {
      const newProject = {
        ...projectForm,
        technologies: projectForm.technologies
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
        createdAt: serverTimestamp(),
      };

      await addDoc(collection(db, 'projects'), newProject);

      setProjectForm(emptyProject);
      setShowProjectForm(false);
      await fetchData();

      alert('Project published successfully.');
    } catch (error) {
      console.error('Error adding project:', error);
      alert('Failed to add project.');
    }
  };

  /* =========================
     ADD REVIEW
  ========================= */

  const handleAddReview = async (e) => {
    e.preventDefault();

    try {
      const customerName = reviewForm.customerName.trim();
      const company = reviewForm.company.trim();
      const service = reviewForm.service.trim();
      const reviewText = reviewForm.review.trim();

      if (customerName.length < 2) {
        alert("Please enter a valid customer name.");
        return;
      }

      if (reviewText.length < 15) {
        alert("Review must contain at least 15 characters.");
        return;
      }

      await addDoc(collection(db, "reviews"), {
        customerName,
        company,
        service,
        review: reviewText,
        rating: Number(reviewForm.rating),
        photoUrl: reviewForm.photoUrl.trim(),

        // IMPORTANT:
        // Every new review starts as pending.
        approved: false,
        published: false,
        featured: false,
        status: "pending",

        createdAt: serverTimestamp(),
        timestamp: serverTimestamp(),
      });

      setReviewForm(emptyReview);
      setShowReviewForm(false);

      await fetchData();

      alert(
        "Customer review added successfully. It is now waiting for approval."
      );
    } catch (error) {
      console.error("Error adding review:", error);

      alert(
        `Failed to add review.\n\n${error?.message || "Unknown Firebase error"}`
      );
    }
  };
  // ==========================================
  // APPROVE REVIEW
  // ==========================================
  const handleApproveReview = async (reviewId) => {
    try {
      await updateDoc(doc(db, "reviews", reviewId), {
        approved: true,
        published: true,
        status: "published",
      });

      await fetchData();

      alert("Review approved and published successfully.");
    } catch (error) {
      console.error("Error approving review:", error);

      alert(
        `Failed to approve review.\n\n${error?.message || "Unknown Firebase error"}`
      );
    }
  };


  // ==========================================
  // UNPUBLISH REVIEW
  // ==========================================
  const handleUnpublishReview = async (reviewId) => {
    try {
      await updateDoc(doc(db, "reviews", reviewId), {
        approved: true,
        published: false,
        status: "unpublished",
      });

      await fetchData();

      alert("Review has been unpublished.");
    } catch (error) {
      console.error("Error unpublishing review:", error);

      alert(
        `Failed to unpublish review.\n\n${error?.message || "Unknown Firebase error"}`
      );
    }
  };


  // ==========================================
  // HIDE / REJECT REVIEW
  // ==========================================
  const handleHideReview = async (reviewId) => {
    try {
      await updateDoc(doc(db, "reviews", reviewId), {
        approved: false,
        published: false,
        status: "hidden",
      });

      await fetchData();

      alert("Review has been hidden.");
    } catch (error) {
      console.error("Error hiding review:", error);

      alert(
        `Failed to hide review.\n\n${error?.message || "Unknown Firebase error"}`
      );
    }
  };
  /* =========================
     ADD POST
  ========================= */

  const handleAddPost = async (e) => {
    e.preventDefault();

    try {
      await addDoc(collection(db, 'posts'), {
        ...postForm,
        createdAt: serverTimestamp(),
      });

      setPostForm(emptyPost);
      setShowPostForm(false);
      await fetchData();

      alert('Post published successfully.');
    } catch (error) {
      console.error('Error adding post:', error);
      alert('Failed to add post.');
    }
  };

  /* =========================
     LOGOUT
  ========================= */

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/alogin');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  /* =========================
     NAVIGATION
  ========================= */

  const changeSection = (section) => {
    setActiveSection(section);
    setSidebarOpen(false);
    setSearchTerm('');
  };

  /* =========================
     STATS
  ========================= */

  const stats = useMemo(
    () => [
      {
        title: 'Projects',
        value: projects.length,
        icon: '📁',
        color: 'blue',
        section: 'projects',
      },
      {
        title: 'Bookings',
        value: bookings.length,
        icon: '📅',
        color: 'purple',
        section: 'bookings',
      },
      {
        title: 'Reviews',
        value: reviews.length,
        icon: '⭐',
        color: 'orange',
        section: 'reviews',
      },
      {
        title: 'Messages',
        value: contacts.length,
        icon: '💬',
        color: 'green',
        section: 'contacts',
      },
    ],
    [projects, bookings, reviews, contacts]
  );

  /* =========================
     FILTERING
  ========================= */

  const filteredProjects = projects.filter((project) =>
    `${project.title || ''} ${project.category || ''} ${project.client || ''}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const filteredReviews = reviews.filter((review) =>
    `${review.customerName || ''} ${review.company || ''} ${review.service || ''}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const filteredPosts = posts.filter((post) =>
    `${post.title || ''} ${post.category || ''}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const recentProjects = [...projects].slice(-5).reverse();
  const recentBookings = [...bookings].slice(-5).reverse();

  const renderStars = (rating = 5) => {
    const safeRating = Math.min(5, Math.max(0, Number(rating)));

    return (
      <span className="stars">
        {'★'.repeat(safeRating)}
        <span className="empty-stars">
          {'★'.repeat(5 - safeRating)}
        </span>
      </span>
    );
  };

  /* =========================
     SIDEBAR
  ========================= */

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: '📊',
    },
    {
      id: 'projects',
      label: 'Projects',
      icon: '📁',
    },
    {
      id: 'reviews',
      label: 'Customer Reviews',
      icon: '⭐',
    },
    {
      id: 'posts',
      label: 'Posts',
      icon: '📝',
    },
    {
      id: 'bookings',
      label: 'Bookings',
      icon: '📅',
    },
    {
      id: 'contacts',
      label: 'Messages',
      icon: '📬',
    },
    {
      id: 'interns',
      label: 'Interns',
      icon: '👨‍💻',
    },
    {
      id: 'certificates',
      label: 'Certificates',
      icon: '📜',
    },
  ];

  return (
    <div className="admin-layout">
      {/* MOBILE OVERLAY */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* SIDEBAR */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <div className="brand-logo">S</div>

          <div>
            <h2>SamSoftix</h2>
            <span>Admin Panel</span>
          </div>

          <button
            className="mobile-close"
            onClick={() => setSidebarOpen(false)}
          >
            ×
          </button>
        </div>

        <div className="sidebar-menu">
          <p className="menu-label">MAIN MENU</p>

          {menuItems.map((item) => (
            <button
              key={item.id}
              className={`sidebar-link ${activeSection === item.id ? 'active' : ''
                }`}
              onClick={() => changeSection(item.id)}
            >
              <span className="sidebar-icon">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>

        <div className="sidebar-bottom">
          <div className="sidebar-help">
            <span>💡</span>
            <div>
              <strong>SamSoftix CMS</strong>
              <small>Manage your website</small>
            </div>
          </div>

          <button className="sidebar-logout" onClick={handleLogout}>
            <span>↪</span>
            Logout
          </button>
        </div>
      </aside>

      {/* MAIN AREA */}
      <main className="admin-main">
        {/* TOP HEADER */}
        <header className="admin-topbar">
          <div className="topbar-left">
            <button
              className="mobile-menu-btn"
              onClick={() => setSidebarOpen(true)}
            >
              ☰
            </button>

            <div>
              <span className="breadcrumb">SamSoftix / Admin</span>
              <h1>
                {activeSection === 'dashboard'
                  ? 'Dashboard'
                  : menuItems.find((item) => item.id === activeSection)
                    ?.label}
              </h1>
            </div>
          </div>

          <div className="topbar-right">
            <div className="admin-user">
              <div className="admin-avatar">A</div>

              <div className="admin-user-info">
                <strong>Administrator</strong>
                <span>Super Admin</span>
              </div>
            </div>

            <button
              className="topbar-logout"
              onClick={handleLogout}
              title="Logout"
            >
              ↪
            </button>
          </div>
        </header>

        <div className="admin-content">
          {loading ? (
            <div className="loading-screen">
              <div className="loading-spinner"></div>
              <p>Loading dashboard...</p>
            </div>
          ) : (
            <>
              {/* ================= DASHBOARD ================= */}
              {activeSection === 'dashboard' && (
                <>
                  <section className="welcome-banner">
                    <div>
                      <span className="welcome-label">
                        ADMINISTRATION PANEL
                      </span>

                      <h2>Welcome back, Administrator 👋</h2>

                      <p>
                        Manage your SamSoftix website, projects, customers,
                        content and certificates from one place.
                      </p>
                    </div>

                    <div className="welcome-decoration">
                      <span>⚡</span>
                    </div>
                  </section>

                  {/* STATS */}
                  <div className="stats-grid">
                    {stats.map((stat) => (
                      <button
                        className="stat-card"
                        key={stat.title}
                        onClick={() => changeSection(stat.section)}
                      >
                        <div className={`stat-icon ${stat.color}`}>
                          {stat.icon}
                        </div>

                        <div className="stat-info">
                          <span>{stat.title}</span>
                          <strong>{stat.value}</strong>
                        </div>

                        <span className="stat-arrow">→</span>
                      </button>
                    ))}
                  </div>

                  {/* QUICK ACTIONS */}
                  <section className="content-card">
                    <div className="section-heading">
                      <div>
                        <span className="section-kicker">QUICK ACTIONS</span>
                        <h2>Manage Website</h2>
                      </div>
                    </div>

                    <div className="quick-actions">
                      <button
                        onClick={() => {
                          changeSection('projects');
                          setShowProjectForm(true);
                        }}
                      >
                        <span>📁</span>
                        <strong>Add Project</strong>
                        <small>Publish portfolio work</small>
                      </button>

                      <button
                        onClick={() => {
                          changeSection('reviews');
                          setShowReviewForm(true);
                        }}
                      >
                        <span>⭐</span>
                        <strong>Add Review</strong>
                        <small>Add customer feedback</small>
                      </button>

                      <button
                        onClick={() => {
                          changeSection('posts');
                          setShowPostForm(true);
                        }}
                      >
                        <span>📝</span>
                        <strong>Create Post</strong>
                        <small>Publish new content</small>
                      </button>

                      <button onClick={() => changeSection('bookings')}>
                        <span>📅</span>
                        <strong>View Bookings</strong>
                        <small>Manage client requests</small>
                      </button>
                    </div>
                  </section>

                  <div className="dashboard-columns">
                    {/* RECENT PROJECTS */}
                    <section className="content-card">
                      <div className="section-heading">
                        <div>
                          <span className="section-kicker">PORTFOLIO</span>
                          <h2>Recent Projects</h2>
                        </div>

                        <button
                          className="text-button"
                          onClick={() => changeSection('projects')}
                        >
                          View All →
                        </button>
                      </div>

                      {recentProjects.length === 0 ? (
                        <div className="empty-state compact">
                          <span>📁</span>
                          <p>No projects added yet.</p>
                        </div>
                      ) : (
                        <div className="mini-project-list">
                          {recentProjects.map((project) => (
                            <div className="mini-project" key={project.id}>
                              <div className="mini-project-image">
                                {project.imageUrl ? (
                                  <img
                                    src={project.imageUrl}
                                    alt={project.title}
                                  />
                                ) : (
                                  <span>📁</span>
                                )}
                              </div>

                              <div className="mini-project-info">
                                <strong>{project.title}</strong>
                                <span>
                                  {project.category || 'Project'}
                                </span>
                              </div>

                              <span
                                className={`status-badge ${project.status === 'Ongoing'
                                    ? 'ongoing'
                                    : 'completed'
                                  }`}
                              >
                                {project.status || 'Completed'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>

                    {/* RECENT BOOKINGS */}
                    <section className="content-card">
                      <div className="section-heading">
                        <div>
                          <span className="section-kicker">CLIENTS</span>
                          <h2>Recent Bookings</h2>
                        </div>

                        <button
                          className="text-button"
                          onClick={() => changeSection('bookings')}
                        >
                          View All →
                        </button>
                      </div>

                      {recentBookings.length === 0 ? (
                        <div className="empty-state compact">
                          <span>📅</span>
                          <p>No bookings yet.</p>
                        </div>
                      ) : (
                        <div className="mini-booking-list">
                          {recentBookings.map((booking) => (
                            <div
                              className="mini-booking"
                              key={booking.id}
                            >
                              <div className="client-avatar">
                                {(booking.name || 'C')
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>

                              <div className="mini-booking-info">
                                <strong>
                                  {booking.name || 'Unknown Client'}
                                </strong>

                                <span>
                                  {booking.service || 'Service Request'}
                                </span>
                              </div>

                              <span className="booking-budget">
                                {booking.budget || '—'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  </div>
                </>
              )}

              {/* ================= PROJECTS ================= */}
              {activeSection === 'projects' && (
                <section className="content-card full-width-card">
                  <div className="section-heading">
                    <div>
                      <span className="section-kicker">PORTFOLIO CMS</span>
                      <h2>Projects</h2>
                      <p>
                        Add and manage projects displayed on your website.
                      </p>
                    </div>

                    <button
                      className="primary-button"
                      onClick={() =>
                        setShowProjectForm((previous) => !previous)
                      }
                    >
                      {showProjectForm ? '× Close Form' : '+ Add Project'}
                    </button>
                  </div>

                  {showProjectForm && (
                    <form
                      className="management-form"
                      onSubmit={handleAddProject}
                    >
                      <div className="form-title">
                        <span>📁</span>
                        <div>
                          <h3>Add New Project</h3>
                          <p>
                            Add a project to your SamSoftix portfolio.
                          </p>
                        </div>
                      </div>

                      <div className="form-grid">
                        <div className="form-group">
                          <label>Project Title *</label>
                          <input
                            name="title"
                            value={projectForm.title}
                            onChange={handleProjectChange}
                            placeholder="e.g. Restaurant Booking Website"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>Category *</label>
                          <input
                            name="category"
                            value={projectForm.category}
                            onChange={handleProjectChange}
                            placeholder="Web Development"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>Client</label>
                          <input
                            name="client"
                            value={projectForm.client}
                            onChange={handleProjectChange}
                            placeholder="Client / Business Name"
                          />
                        </div>

                        <div className="form-group">
                          <label>Status</label>
                          <select
                            name="status"
                            value={projectForm.status}
                            onChange={handleProjectChange}
                          >
                            <option value="Completed">Completed</option>
                            <option value="Ongoing">Ongoing</option>
                          </select>
                        </div>

                        <div className="form-group full">
                          <label>Description *</label>
                          <textarea
                            name="description"
                            value={projectForm.description}
                            onChange={handleProjectChange}
                            placeholder="Describe the project..."
                            rows="4"
                            required
                          />
                        </div>

                        <div className="form-group full">
                          <label>Technologies</label>
                          <input
                            name="technologies"
                            value={projectForm.technologies}
                            onChange={handleProjectChange}
                            placeholder="React, Firebase, Node.js, MongoDB"
                          />
                          <small>
                            Separate technologies with commas.
                          </small>
                        </div>

                        <div className="form-group">
                          <label>Project Image URL</label>
                          <input
                            type="url"
                            name="imageUrl"
                            value={projectForm.imageUrl}
                            onChange={handleProjectChange}
                            placeholder="https://..."
                          />
                        </div>

                        <div className="form-group">
                          <label>Live Project URL</label>
                          <input
                            type="url"
                            name="liveUrl"
                            value={projectForm.liveUrl}
                            onChange={handleProjectChange}
                            placeholder="https://..."
                          />
                        </div>

                        <div className="form-group">
                          <label>GitHub URL</label>
                          <input
                            type="url"
                            name="githubUrl"
                            value={projectForm.githubUrl}
                            onChange={handleProjectChange}
                            placeholder="https://github.com/..."
                          />
                        </div>

                        <label className="checkbox-card">
                          <input
                            type="checkbox"
                            name="featured"
                            checked={projectForm.featured}
                            onChange={handleProjectChange}
                          />
                          <span>
                            <strong>Featured Project</strong>
                            <small>
                              Highlight this project on the website.
                            </small>
                          </span>
                        </label>
                      </div>

                      <div className="form-actions">
                        <button
                          type="button"
                          className="secondary-button"
                          onClick={() => {
                            setShowProjectForm(false);
                            setProjectForm(emptyProject);
                          }}
                        >
                          Cancel
                        </button>

                        <button type="submit" className="primary-button">
                          Publish Project
                        </button>
                      </div>
                    </form>
                  )}

                  <div className="list-toolbar">
                    <div className="search-box">
                      <span>⌕</span>
                      <input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search projects..."
                      />
                    </div>

                    <span className="result-count">
                      {filteredProjects.length} projects
                    </span>
                  </div>

                  {filteredProjects.length === 0 ? (
                    <div className="empty-state">
                      <span>📁</span>
                      <h3>No projects found</h3>
                      <p>Add your first project to your portfolio.</p>
                    </div>
                  ) : (
                    <div className="projects-grid">
                      {filteredProjects.map((project) => (
                        <article className="project-card" key={project.id}>
                          <div className="project-image">
                            {project.imageUrl ? (
                              <img
                                src={project.imageUrl}
                                alt={project.title}
                              />
                            ) : (
                              <div className="project-placeholder">
                                📁
                              </div>
                            )}

                            {project.featured && (
                              <span className="featured-badge">
                                ★ Featured
                              </span>
                            )}
                          </div>

                          <div className="project-body">
                            <div className="project-top">
                              <span className="project-category">
                                {project.category || 'Project'}
                              </span>

                              <span
                                className={`status-badge ${project.status === 'Ongoing'
                                    ? 'ongoing'
                                    : 'completed'
                                  }`}
                              >
                                {project.status || 'Completed'}
                              </span>
                            </div>

                            <h3>{project.title}</h3>

                            <p>
                              {project.description ||
                                'No project description.'}
                            </p>

                            {project.client && (
                              <div className="project-client">
                                <span>Client:</span>
                                <strong>{project.client}</strong>
                              </div>
                            )}

                            {Array.isArray(project.technologies) &&
                              project.technologies.length > 0 && (
                                <div className="technology-list">
                                  {project.technologies.map(
                                    (technology, index) => (
                                      <span key={index}>
                                        {technology}
                                      </span>
                                    )
                                  )}
                                </div>
                              )}

                            <div className="project-actions">
                              {project.liveUrl && (
                                <a
                                  href={project.liveUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="outline-button"
                                >
                                  Live ↗
                                </a>
                              )}

                              {project.githubUrl && (
                                <a
                                  href={project.githubUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="outline-button"
                                >
                                  GitHub ↗
                                </a>
                              )}

                              <button
                                className="danger-outline"
                                onClick={() =>
                                  handleDeleteProject(project.id)
                                }
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </section>
              )}

              {/* ================= REVIEWS ================= */}
              {/* ================= REVIEWS ================= */}
{activeSection === "reviews" && (
  <section className="content-card full-width-card">

    {/* ================= HEADER ================= */}
    <div className="section-heading">

      <div>
        <span className="section-kicker">
          CUSTOMER FEEDBACK
        </span>

        <h2>Customer Reviews</h2>

        <p>
          Manage customer testimonials and control which reviews
          are published on your website.
        </p>
      </div>

      <button
        type="button"
        className="primary-button"
        onClick={() =>
          setShowReviewForm((previous) => !previous)
        }
      >
        {showReviewForm
          ? "× Close Form"
          : "+ Add Review"}
      </button>

    </div>


    {/* =====================================================
        ADD REVIEW FORM
    ===================================================== */}

    {showReviewForm && (
      <form
        className="management-form"
        onSubmit={handleAddReview}
      >

        <div className="form-title">

          <span>⭐</span>

          <div>
            <h3>Add Customer Review</h3>

            <p>
              Add a customer testimonial. New reviews are
              automatically saved as pending until approved.
            </p>
          </div>

        </div>


        <div className="form-grid">

          {/* CUSTOMER NAME */}
          <div className="form-group">

            <label>
              Customer Name *
            </label>

            <input
              type="text"
              name="customerName"
              value={reviewForm.customerName}
              onChange={handleReviewChange}
              placeholder="Customer name"
              required
            />

          </div>


          {/* COMPANY */}
          <div className="form-group">

            <label>
              Company / Business
            </label>

            <input
              type="text"
              name="company"
              value={reviewForm.company}
              onChange={handleReviewChange}
              placeholder="Company name"
            />

          </div>


          {/* SERVICE */}
          <div className="form-group">

            <label>
              Service
            </label>

            <input
              type="text"
              name="service"
              value={reviewForm.service}
              onChange={handleReviewChange}
              placeholder="Website Development"
            />

          </div>


          {/* RATING */}
          <div className="form-group">

            <label>
              Rating
            </label>

            <select
              name="rating"
              value={reviewForm.rating}
              onChange={handleReviewChange}
            >
              <option value="5">
                ★★★★★ 5
              </option>

              <option value="4">
                ★★★★☆ 4
              </option>

              <option value="3">
                ★★★☆☆ 3
              </option>

              <option value="2">
                ★★☆☆☆ 2
              </option>

              <option value="1">
                ★☆☆☆☆ 1
              </option>
            </select>

          </div>


          {/* PHOTO */}
          <div className="form-group">

            <label>
              Customer Photo URL
            </label>

            <input
              type="url"
              name="photoUrl"
              value={reviewForm.photoUrl}
              onChange={handleReviewChange}
              placeholder="https://..."
            />

          </div>


          {/* REVIEW */}
          <div className="form-group full">

            <label>
              Review *
            </label>

            <textarea
              name="review"
              value={reviewForm.review}
              onChange={handleReviewChange}
              placeholder="Write customer feedback..."
              rows="5"
              required
            />

          </div>


          {/* FEATURED */}
          <label className="checkbox-card">

            <input
              type="checkbox"
              name="featured"
              checked={reviewForm.featured}
              onChange={handleReviewChange}
            />

            <span>

              <strong>
                Featured Review
              </strong>

              <small>
                Highlight this review after it has been approved.
              </small>

            </span>

          </label>


          {/* APPROVAL INFORMATION */}
          <div className="review-approval-info">

            <div className="approval-icon">
              🔒
            </div>

            <div>

              <strong>
                Approval Required
              </strong>

              <small>
                New reviews are automatically saved as
                <b> Pending Approval</b>.
                They will not appear publicly until an
                administrator approves them.
              </small>

            </div>

          </div>

        </div>


        {/* FORM ACTIONS */}
        <div className="form-actions">

          <button
            type="button"
            className="secondary-button"
            onClick={() => {
              setShowReviewForm(false);
              setReviewForm(emptyReview);
            }}
          >
            Cancel
          </button>


          <button
            type="submit"
            className="primary-button"
          >
            Add Review
          </button>

        </div>

      </form>
    )}


    {/* =====================================================
        SEARCH
    ===================================================== */}

    <div className="list-toolbar">

      <div className="search-box">

        <span>⌕</span>

        <input
          value={searchTerm}
          onChange={(e) =>
            setSearchTerm(e.target.value)
          }
          placeholder="Search reviews..."
        />

      </div>


      <span className="result-count">
        {filteredReviews.length} reviews
      </span>

    </div>


    {/* =====================================================
        EMPTY STATE
    ===================================================== */}

    {filteredReviews.length === 0 ? (

      <div className="empty-state">

        <span>⭐</span>

        <h3>
          No customer reviews
        </h3>

        <p>
          Add your first customer testimonial.
        </p>

      </div>

    ) : (

      /* ===================================================
         REVIEW GRID
      =================================================== */

      <div className="reviews-grid">

        {filteredReviews.map((review) => (

          <article
            className="review-card"
            key={review.id}
          >

            {/* ================= HEADER ================= */}

            <div className="review-card-header">

              <div className="review-person">

                {review.photoUrl ? (

                  <img
                    src={review.photoUrl}
                    alt={
                      review.customerName ||
                      "Customer"
                    }
                  />

                ) : (

                  <div className="review-avatar">

                    {(
                      review.customerName ||
                      "C"
                    )
                      .charAt(0)
                      .toUpperCase()}

                  </div>

                )}


                <div>

                  <strong>
                    {review.customerName ||
                      "Customer"}
                  </strong>

                  <span>
                    {review.company ||
                      review.service ||
                      "Customer"}
                  </span>

                </div>

              </div>


              {/* RATING */}

              <div className="review-rating">
                {renderStars(review.rating)}
              </div>

            </div>


            {/* ================= REVIEW ================= */}

            <p className="review-text">
              “{review.review}”
            </p>


            {/* ================= META ================= */}

            <div className="review-meta">

              <span>
                {review.service ||
                  "Service"}
              </span>


              {/* FEATURED */}

              {review.featured && (

                <span className="small-badge featured">
                  ★ Featured
                </span>

              )}


              {/* STATUS */}

              <span
                className={`small-badge ${
                  review.status === "published"
                    ? "published"
                    : review.status === "hidden"
                    ? "unpublished"
                    : "pending"
                }`}
              >

                {review.status === "published"
                  ? "Published"
                  : review.status === "hidden"
                  ? "Hidden"
                  : "Pending Approval"}

              </span>

            </div>


            {/* =================================================
                APPROVAL ACTIONS
            ================================================= */}

            <div className="review-admin-actions">


              {/* APPROVE */}

              {review.status !== "published" && (

                <button
                  type="button"
                  className="approve-review-button"
                  onClick={() =>
                    handleApproveReview(
                      review.id
                    )
                  }
                >
                  ✓ Approve & Publish
                </button>

              )}


              {/* UNPUBLISH */}

              {review.status === "published" && (

                <button
                  type="button"
                  className="unpublish-review-button"
                  onClick={() =>
                    handleUnpublishReview(
                      review.id
                    )
                  }
                >
                  ⏸ Unpublish
                </button>

              )}


              {/* HIDE */}

              {review.status !== "hidden" && (

                <button
                  type="button"
                  className="hide-review-button"
                  onClick={() =>
                    handleHideReview(
                      review.id
                    )
                  }
                >
                  Hide
                </button>

              )}


              {/* DELETE */}

              <button
                type="button"
                className="danger-outline full-danger"
                onClick={() =>
                  handleDeleteReview(
                    review.id
                  )
                }
              >
                Delete Review
              </button>

            </div>

          </article>

        ))}

      </div>

    )}

  </section>
)}

              {/* ================= POSTS ================= */}
              {activeSection === 'posts' && (
                <section className="content-card full-width-card">
                  <div className="section-heading">
                    <div>
                      <span className="section-kicker">
                        CONTENT MANAGEMENT
                      </span>
                      <h2>Posts & Articles</h2>
                      <p>
                        Manage promotional posts, announcements and
                        website content.
                      </p>
                    </div>

                    <button
                      className="primary-button"
                      onClick={() =>
                        setShowPostForm((previous) => !previous)
                      }
                    >
                      {showPostForm ? '× Close Form' : '+ Create Post'}
                    </button>
                  </div>

                  {showPostForm && (
                    <form
                      className="management-form"
                      onSubmit={handleAddPost}
                    >
                      <div className="form-title">
                        <span>📝</span>
                        <div>
                          <h3>Create New Post</h3>
                          <p>
                            Publish an article, announcement or
                            promotional post.
                          </p>
                        </div>
                      </div>

                      <div className="form-grid">
                        <div className="form-group full">
                          <label>Post Title *</label>
                          <input
                            name="title"
                            value={postForm.title}
                            onChange={handlePostChange}
                            placeholder="Enter post title"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>Category</label>
                          <input
                            name="category"
                            value={postForm.category}
                            onChange={handlePostChange}
                            placeholder="Technology / News / Offer"
                          />
                        </div>

                        <div className="form-group">
                          <label>Image URL</label>
                          <input
                            type="url"
                            name="imageUrl"
                            value={postForm.imageUrl}
                            onChange={handlePostChange}
                            placeholder="https://..."
                          />
                        </div>

                        <div className="form-group full">
                          <label>Short Description</label>
                          <textarea
                            name="excerpt"
                            value={postForm.excerpt}
                            onChange={handlePostChange}
                            placeholder="Short description for cards..."
                            rows="3"
                          />
                        </div>

                        <div className="form-group full">
                          <label>Post Content *</label>
                          <textarea
                            name="content"
                            value={postForm.content}
                            onChange={handlePostChange}
                            placeholder="Write your post content..."
                            rows="8"
                            required
                          />
                        </div>

                        <div className="form-group full">
                          <label>External / Full Post Link</label>
                          <input
                            type="url"
                            name="link"
                            value={postForm.link}
                            onChange={handlePostChange}
                            placeholder="https://..."
                          />
                        </div>

                        <label className="checkbox-card">
                          <input
                            type="checkbox"
                            name="published"
                            checked={postForm.published}
                            onChange={handlePostChange}
                          />

                          <span>
                            <strong>Published</strong>
                            <small>
                              Make this post visible on the website.
                            </small>
                          </span>
                        </label>
                      </div>

                      <div className="form-actions">
                        <button
                          type="button"
                          className="secondary-button"
                          onClick={() => {
                            setShowPostForm(false);
                            setPostForm(emptyPost);
                          }}
                        >
                          Cancel
                        </button>

                        <button type="submit" className="primary-button">
                          Publish Post
                        </button>
                      </div>
                    </form>
                  )}

                  <div className="list-toolbar">
                    <div className="search-box">
                      <span>⌕</span>
                      <input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search posts..."
                      />
                    </div>

                    <span className="result-count">
                      {filteredPosts.length} posts
                    </span>
                  </div>

                  {filteredPosts.length === 0 ? (
                    <div className="empty-state">
                      <span>📝</span>
                      <h3>No posts found</h3>
                      <p>Create your first website post.</p>
                    </div>
                  ) : (
                    <div className="posts-grid">
                      {filteredPosts.map((post) => (
                        <article className="post-card" key={post.id}>
                          <div className="post-image">
                            {post.imageUrl ? (
                              <img
                                src={post.imageUrl}
                                alt={post.title}
                              />
                            ) : (
                              <div className="post-placeholder">
                                📝
                              </div>
                            )}
                          </div>

                          <div className="post-body">
                            <div className="post-meta">
                              <span>
                                {post.category || 'General'}
                              </span>

                              <span
                                className={`small-badge ${post.published
                                    ? 'published'
                                    : 'unpublished'
                                  }`}
                              >
                                {post.published
                                  ? 'Published'
                                  : 'Draft'}
                              </span>
                            </div>

                            <h3>{post.title}</h3>

                            <p>
                              {post.excerpt ||
                                post.content ||
                                'No description available.'}
                            </p>

                            <div className="post-actions">
                              {post.link && (
                                <a
                                  href={post.link}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="outline-button"
                                >
                                  Open Link ↗
                                </a>
                              )}

                              <button
                                className="danger-outline"
                                onClick={() =>
                                  handleDeletePost(post.id)
                                }
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </section>
              )}

              {/* ================= BOOKINGS ================= */}
              {activeSection === 'bookings' && (
                <section className="content-card full-width-card">
                  <div className="section-heading">
                    <div>
                      <span className="section-kicker">CLIENT REQUESTS</span>
                      <h2>Bookings</h2>
                      <p>Review incoming project requests.</p>
                    </div>

                    <span className="header-count">
                      {bookings.length} Total
                    </span>
                  </div>

                  {bookings.length === 0 ? (
                    <div className="empty-state">
                      <span>📅</span>
                      <h3>No bookings</h3>
                      <p>There are currently no booking requests.</p>
                    </div>
                  ) : (
                    <div className="table-wrapper">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Client</th>
                            <th>Contact</th>
                            <th>Service</th>
                            <th>Budget</th>
                            <th>Timeline</th>
                            <th>Method</th>
                            <th>Message</th>
                            <th>Action</th>
                          </tr>
                        </thead>

                        <tbody>
                          {bookings.map((booking) => (
                            <tr key={booking.id}>
                              <td data-label="Client">
                                <div className="table-person">
                                  <div className="table-avatar">
                                    {(booking.name || 'C')
                                      .charAt(0)
                                      .toUpperCase()}
                                  </div>

                                  <strong>
                                    {booking.name || 'Unknown'}
                                  </strong>
                                </div>
                              </td>

                              <td data-label="Contact">
                                <div className="contact-cell">
                                  <span>{booking.email || '—'}</span>
                                  <span>{booking.phone || '—'}</span>
                                </div>
                              </td>

                              <td data-label="Service">
                                <span className="table-tag">
                                  {booking.service || '—'}
                                </span>
                              </td>

                              <td data-label="Budget">
                                {booking.budget || '—'}
                              </td>

                              <td data-label="Timeline">
                                {booking.timeline || '—'}
                              </td>

                              <td data-label="Method">
                                {booking.contactMethod || '—'}
                              </td>

                              <td data-label="Message">
                                <div className="message-preview">
                                  {booking.message || '—'}
                                </div>
                              </td>

                              <td data-label="Action">
                                <button
                                  className="danger-outline"
                                  onClick={() =>
                                    handleDeleteBooking(booking.id)
                                  }
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              )}

              {/* ================= CONTACTS ================= */}
              {activeSection === 'contacts' && (
                <section className="content-card full-width-card">
                  <div className="section-heading">
                    <div>
                      <span className="section-kicker">
                        COMMUNICATIONS
                      </span>
                      <h2>Contact Messages</h2>
                      <p>Messages submitted through your website.</p>
                    </div>

                    <span className="header-count">
                      {contacts.length} Messages
                    </span>
                  </div>

                  {contacts.length === 0 ? (
                    <div className="empty-state">
                      <span>📬</span>
                      <h3>No messages</h3>
                      <p>No contact submissions have been received.</p>
                    </div>
                  ) : (
                    <div className="table-wrapper">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Message</th>
                            <th>Action</th>
                          </tr>
                        </thead>

                        <tbody>
                          {contacts.map((contact) => (
                            <tr key={contact.id}>
                              <td data-label="Name">
                                <div className="table-person">
                                  <div className="table-avatar">
                                    {(contact.name || 'C')
                                      .charAt(0)
                                      .toUpperCase()}
                                  </div>

                                  <strong>
                                    {contact.name || 'Unknown'}
                                  </strong>
                                </div>
                              </td>

                              <td data-label="Email">
                                {contact.email || '—'}
                              </td>

                              <td data-label="Phone">
                                {contact.phone || 'N/A'}
                              </td>

                              <td data-label="Message">
                                <div className="message-preview">
                                  {contact.message || '—'}
                                </div>
                              </td>

                              <td data-label="Action">
                                <button
                                  className="danger-outline"
                                  onClick={() =>
                                    handleDeleteContact(contact.id)
                                  }
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              )}

              {/* ================= INTERNS ================= */}
              {activeSection === 'interns' && (
                <section className="content-card full-width-card">
                  <div className="section-heading">
                    <div>
                      <span className="section-kicker">
                        TEAM MANAGEMENT
                      </span>
                      <h2>Interns</h2>
                      <p>Manage intern records and completed work.</p>
                    </div>
                  </div>

                  <form
                    className="management-form compact-form"
                    onSubmit={handleAddIntern}
                  >
                    <div className="form-title">
                      <span>👨‍💻</span>
                      <div>
                        <h3>Add Intern</h3>
                        <p>Add a new intern record.</p>
                      </div>
                    </div>

                    <div className="form-grid">
                      <div className="form-group">
                        <label>Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={internForm.name}
                          onChange={handleInternChange}
                          placeholder="Intern name"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Domain *</label>
                        <input
                          type="text"
                          name="domain"
                          value={internForm.domain}
                          onChange={handleInternChange}
                          placeholder="Web Development"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Joining Date *</label>
                        <input
                          type="date"
                          name="joiningDate"
                          value={internForm.joiningDate}
                          onChange={handleInternChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Ending Date *</label>
                        <input
                          type="date"
                          name="endingDate"
                          value={internForm.endingDate}
                          onChange={handleInternChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Responsibilities *</label>
                        <input
                          type="text"
                          name="responsibilities"
                          value={internForm.responsibilities}
                          onChange={handleInternChange}
                          placeholder="HTML, CSS, React"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Projects *</label>
                        <input
                          type="text"
                          name="projects"
                          value={internForm.projects}
                          onChange={handleInternChange}
                          placeholder="Website, Dashboard"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-actions">
                      <button type="submit" className="primary-button">
                        Add Intern
                      </button>
                    </div>
                  </form>

                  {interns.length === 0 ? (
                    <div className="empty-state">
                      <span>👨‍💻</span>
                      <h3>No interns</h3>
                      <p>No intern records have been added.</p>
                    </div>
                  ) : (
                    <div className="table-wrapper">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Dates</th>
                            <th>Domain</th>
                            <th>Responsibilities</th>
                            <th>Projects</th>
                            <th>Action</th>
                          </tr>
                        </thead>

                        <tbody>
                          {interns.map((intern) => (
                            <tr key={intern.id}>
                              <td data-label="Name">
                                <div className="table-person">
                                  <div className="table-avatar">
                                    {(intern.name || 'I')
                                      .charAt(0)
                                      .toUpperCase()}
                                  </div>

                                  <strong>{intern.name}</strong>
                                </div>
                              </td>

                              <td data-label="Dates">
                                <div className="date-cell">
                                  <span>
                                    {intern.joiningDate || '—'}
                                  </span>
                                  <small>
                                    to {intern.endingDate || '—'}
                                  </small>
                                </div>
                              </td>

                              <td data-label="Domain">
                                <span className="table-tag">
                                  {intern.domain || '—'}
                                </span>
                              </td>

                              <td data-label="Responsibilities">
                                {Array.isArray(intern.responsibilities)
                                  ? intern.responsibilities.join(', ')
                                  : '—'}
                              </td>

                              <td data-label="Projects">
                                {Array.isArray(intern.projects)
                                  ? intern.projects.join(', ')
                                  : '—'}
                              </td>

                              <td data-label="Action">
                                <button
                                  className="danger-outline"
                                  onClick={() =>
                                    handleDeleteIntern(intern.id)
                                  }
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              )}

              {/* ================= CERTIFICATES ================= */}
              {activeSection === 'certificates' && (
                <section className="content-card full-width-card">
                  <div className="section-heading">
                    <div>
                      <span className="section-kicker">
                        CERTIFICATION SYSTEM
                      </span>
                      <h2>Certificates</h2>
                      <p>Manage student and training certificates.</p>
                    </div>
                  </div>

                  <form
                    className="management-form"
                    onSubmit={handleAddCertificate}
                  >
                    <div className="form-title">
                      <span>📜</span>
                      <div>
                        <h3>Add Certificate</h3>
                        <p>
                          Create a certificate record for a candidate.
                        </p>
                      </div>
                    </div>

                    <div className="form-grid">
                      <div className="form-group">
                        <label>Candidate Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={certificateForm.name}
                          onChange={handleCertificateChange}
                          placeholder="Candidate name"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Certificate ID *</label>
                        <input
                          type="text"
                          name="certificateId"
                          value={certificateForm.certificateId}
                          onChange={handleCertificateChange}
                          placeholder="SAM-2026-001"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Grade *</label>
                        <input
                          type="text"
                          name="grade"
                          value={certificateForm.grade}
                          onChange={handleCertificateChange}
                          placeholder="A+"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Course Name *</label>
                        <input
                          type="text"
                          name="courseName"
                          value={certificateForm.courseName}
                          onChange={handleCertificateChange}
                          placeholder="Web Development"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Start Date *</label>
                        <input
                          type="date"
                          name="startDate"
                          value={certificateForm.startDate}
                          onChange={handleCertificateChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>End Date *</label>
                        <input
                          type="date"
                          name="endDate"
                          value={certificateForm.endDate}
                          onChange={handleCertificateChange}
                          required
                        />
                      </div>

                      <div className="form-group full">
                        <label>Description *</label>
                        <textarea
                          name="description"
                          value={certificateForm.description}
                          onChange={handleCertificateChange}
                          placeholder="Certificate description"
                          rows="4"
                          required
                        />
                      </div>

                      <label className="checkbox-card">
                        <input
                          type="checkbox"
                          name="feePending"
                          checked={certificateForm.feePending}
                          onChange={handleCertificateChange}
                        />

                        <span>
                          <strong>Fee Pending</strong>
                          <small>
                            Mark this certificate as having pending fees.
                          </small>
                        </span>
                      </label>
                    </div>

                    <div className="form-actions">
                      <button type="submit" className="primary-button">
                        Add Certificate
                      </button>
                    </div>
                  </form>

                  {certificates.length === 0 ? (
                    <div className="empty-state">
                      <span>📜</span>
                      <h3>No certificates</h3>
                      <p>No certificate records have been added.</p>
                    </div>
                  ) : (
                    <div className="table-wrapper">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Candidate</th>
                            <th>Certificate ID</th>
                            <th>Course</th>
                            <th>Grade</th>
                            <th>Dates</th>
                            <th>Fee</th>
                            <th>Action</th>
                          </tr>
                        </thead>

                        <tbody>
                          {certificates.map((certificate) => (
                            <tr key={certificate.id}>
                              <td data-label="Candidate">
                                <div className="table-person">
                                  <div className="table-avatar">
                                    {(certificate.name || 'C')
                                      .charAt(0)
                                      .toUpperCase()}
                                  </div>

                                  <strong>{certificate.name}</strong>
                                </div>
                              </td>

                              <td data-label="Certificate ID">
                                <span className="certificate-id">
                                  {certificate.certificateId}
                                </span>
                              </td>

                              <td data-label="Course">
                                {certificate.courseName}
                              </td>

                              <td data-label="Grade">
                                <span className="grade-badge">
                                  {certificate.grade}
                                </span>
                              </td>

                              <td data-label="Dates">
                                <div className="date-cell">
                                  <span>
                                    {certificate.startDate || '—'}
                                  </span>
                                  <small>
                                    to {certificate.endDate || '—'}
                                  </small>
                                </div>
                              </td>

                              <td data-label="Fee">
                                <span
                                  className={`fee-badge ${certificate.feePending
                                      ? 'pending'
                                      : 'paid'
                                    }`}
                                >
                                  {certificate.feePending
                                    ? 'Pending'
                                    : 'Clear'}
                                </span>
                              </td>

                              <td data-label="Action">
                                <button
                                  className="danger-outline"
                                  onClick={() =>
                                    handleDeleteCertificate(
                                      certificate.id
                                    )
                                  }
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default APage;